// App — chooses Pair vs Kiosk based on persisted device credentials,
// then owns the outbox + flusher so they live for the kiosk session.
//
// On unpair (operator-initiated) or revoke (server returned 401 on a
// flush), the localStorage device entry is cleared and we drop back
// to PairScreen. The OUTBOX is intentionally NOT cleared on those
// events: a school that's been mid-rush when a token rolled should
// not lose captures. The next paired session flushes them with its
// fresh token (the new device row inherits the same gate_id by the
// pair-claim contract, so server-side dedupe still works correctly).

import { useEffect, useMemo, useState } from "react";

import { Client, DeviceClient, type DevicePairResponse } from "@sms/sdk";

import { createFlusher, type Flusher } from "./flusher";
import { KioskScreen } from "./KioskScreen";
import { createOutbox, type OutboxAPI } from "./outbox";
import { PairScreen } from "./PairScreen";
import { clear, load, save, type PersistedDevice } from "./storage";

const env = (import.meta.env ?? {}) as Record<string, string | undefined>;
const baseURL = env.VITE_API_BASE_URL ?? (typeof window !== "undefined" ? window.location.origin : "");

export interface AppProps {
  client?: Pick<Client, "claimDevicePair">;
  deviceClientFor?: (token: string) => Pick<DeviceClient, "recordArrival">;
  initialDevice?: PersistedDevice | null;
  /** Tests inject a stub outbox + flusher. Production wires the real ones. */
  outbox?: OutboxAPI;
  flusher?: Pick<Flusher, "wake" | "start" | "stop">;
}

export function App({
  client,
  deviceClientFor,
  initialDevice,
  outbox: outboxProp,
  flusher: flusherProp,
}: AppProps) {
  const sdkClient = useMemo(() => client ?? new Client({ baseURL }), [client]);
  const [device, setDevice] = useState<PersistedDevice | null>(
    initialDevice ?? load(),
  );

  // The outbox lives across pair / unpair so a token roll doesn't
  // lose mid-rush captures.
  const outbox = useMemo(() => outboxProp ?? createOutbox(), [outboxProp]);

  // The flusher is per device session — it closes over the
  // bearer-auth DeviceClient and we'd rather build a fresh one when
  // the token changes.
  const flusher = useMemo<Pick<Flusher, "wake" | "start" | "stop">>(() => {
    if (flusherProp) return flusherProp;
    if (!device) {
      // No device → no flushing. Return a no-op so call sites don't
      // have to null-check.
      return {
        wake: async () => ({ kind: "skip", reason: "offline" }) as const,
        start: () => undefined,
        stop: () => undefined,
      };
    }
    const dc = deviceClientFor
      ? deviceClientFor(device.api_token)
      : new DeviceClient({ baseURL, apiToken: device.api_token });
    return createFlusher({
      outbox,
      client: dc,
      onRevoked: () => {
        clear();
        setDevice(null);
      },
    });
  }, [flusherProp, device, deviceClientFor, outbox]);

  // Start the timer when paired; stop on unpair so we don't keep
  // hitting the API once the device is gone.
  useEffect(() => {
    if (!device) return;
    flusher.start();
    return () => flusher.stop();
  }, [flusher, device]);

  if (!device) {
    return (
      <PairScreen
        locale="ne"
        client={sdkClient}
        onPaired={(resp: DevicePairResponse) => {
          const persisted: PersistedDevice = {
            api_token: resp.api_token,
            device_id: resp.device_id,
            school_id: resp.school_id,
            label: resp.label,
            gate_id: resp.gate_id,
          };
          save(persisted);
          setDevice(persisted);
        }}
      />
    );
  }

  return (
    <KioskScreen
      locale="ne"
      device={device}
      outbox={outbox}
      flusher={flusher}
      onUnpair={() => {
        clear();
        setDevice(null);
      }}
      onRevoked={() => {
        clear();
        setDevice(null);
      }}
    />
  );
}
